/**
 * DEFCON TERMINAL - Screen Buffer Management
 * Handles 80x24 character buffer with scroll support
 */

import { ASCII_DIMENSIONS } from '@shared/constants';

/** Character cell with optional styling */
export interface CharacterCell {
  char: string;
  className?: string;
  foreground?: string;
  background?: string;
}

/** Screen buffer state for save/restore */
export interface ScreenState {
  buffer: CharacterCell[][];
  cursorX: number;
  cursorY: number;
  scrollOffset: number;
}

/** Screen buffer configuration */
export interface ScreenBufferConfig {
  width?: number;
  height?: number;
  scrollbackSize?: number;
}

/**
 * ScreenBuffer class
 * Manages a 2D character buffer with scrolling capabilities
 */
export class ScreenBuffer {
  private width: number;
  private height: number;
  private scrollbackSize: number;

  private buffer: CharacterCell[][];
  private scrollback: CharacterCell[][];
  private scrollOffset: number;

  private cursorX: number;
  private cursorY: number;

  private savedStates: ScreenState[];

  constructor(width?: number, height?: number, config: ScreenBufferConfig = {}) {
    this.width = width ?? config.width ?? ASCII_DIMENSIONS.terminalWidth;
    this.height = height ?? config.height ?? ASCII_DIMENSIONS.terminalHeight;
    this.scrollbackSize = config.scrollbackSize ?? 1000;

    this.buffer = this.createEmptyBuffer();
    this.scrollback = [];
    this.scrollOffset = 0;

    this.cursorX = 0;
    this.cursorY = 0;

    this.savedStates = [];
  }

  /**
   * Create empty buffer filled with spaces
   */
  private createEmptyBuffer(): CharacterCell[][] {
    const buffer: CharacterCell[][] = [];
    for (let y = 0; y < this.height; y++) {
      buffer.push(this.createEmptyLine());
    }
    return buffer;
  }

  /**
   * Create empty line filled with spaces
   */
  private createEmptyLine(): CharacterCell[] {
    const line: CharacterCell[] = [];
    for (let x = 0; x < this.width; x++) {
      line.push({ char: ' ' });
    }
    return line;
  }

  /**
   * Write character at position
   */
  public write(x: number, y: number, char: string, className?: string): void {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return;
    }

    this.buffer[y][x] = {
      char: char.charAt(0) || ' ',
      className,
    };
  }

  /**
   * Write string starting at position
   */
  public writeString(x: number, y: number, text: string, className?: string): void {
    for (let i = 0; i < text.length && x + i < this.width; i++) {
      this.write(x + i, y, text.charAt(i), className);
    }
  }

  /**
   * Write line at cursor position and advance cursor
   */
  public writeLine(text: string, className?: string): void {
    this.writeString(this.cursorX, this.cursorY, text, className);
    this.newLine();
  }

  /**
   * Move to new line, scrolling if necessary
   */
  public newLine(): void {
    this.cursorX = 0;
    this.cursorY++;

    if (this.cursorY >= this.height) {
      this.scroll();
      this.cursorY = this.height - 1;
    }
  }

  /**
   * Scroll buffer up by one line
   */
  public scroll(): void {
    // Move top line to scrollback
    const topLine = this.buffer.shift();
    if (topLine) {
      this.scrollback.push(topLine);

      // Trim scrollback to max size
      if (this.scrollback.length > this.scrollbackSize) {
        this.scrollback.shift();
      }
    }

    // Add new empty line at bottom
    this.buffer.push(this.createEmptyLine());
  }

  /**
   * Scroll multiple lines
   */
  public scrollLines(count: number): void {
    for (let i = 0; i < count; i++) {
      this.scroll();
    }
  }

  /**
   * Get line from buffer
   */
  public getLine(lineNumber: number): CharacterCell[] | null {
    if (lineNumber < 0 || lineNumber >= this.height) {
      return null;
    }
    return [...this.buffer[lineNumber]];
  }

  /**
   * Get line as string
   */
  public getLineAsString(lineNumber: number): string {
    const line = this.getLine(lineNumber);
    if (!line) return '';
    return line.map(cell => cell.char).join('');
  }

  /**
   * Get character at position
   */
  public getChar(x: number, y: number): CharacterCell | null {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return null;
    }
    return { ...this.buffer[y][x] };
  }

  /**
   * Clear entire buffer
   */
  public clear(): void {
    this.buffer = this.createEmptyBuffer();
    this.cursorX = 0;
    this.cursorY = 0;
  }

  /**
   * Clear line at position
   */
  public clearLine(lineNumber: number): void {
    if (lineNumber >= 0 && lineNumber < this.height) {
      this.buffer[lineNumber] = this.createEmptyLine();
    }
  }

  /**
   * Clear from cursor to end of line
   */
  public clearToEndOfLine(): void {
    for (let x = this.cursorX; x < this.width; x++) {
      this.buffer[this.cursorY][x] = { char: ' ' };
    }
  }

  /**
   * Clear from cursor to end of screen
   */
  public clearToEndOfScreen(): void {
    this.clearToEndOfLine();
    for (let y = this.cursorY + 1; y < this.height; y++) {
      this.clearLine(y);
    }
  }

  /**
   * Save current screen state
   */
  public saveState(): void {
    const state: ScreenState = {
      buffer: this.buffer.map(line => line.map(cell => ({ ...cell }))),
      cursorX: this.cursorX,
      cursorY: this.cursorY,
      scrollOffset: this.scrollOffset,
    };
    this.savedStates.push(state);
  }

  /**
   * Restore last saved screen state
   */
  public restoreState(): boolean {
    const state = this.savedStates.pop();
    if (!state) return false;

    this.buffer = state.buffer;
    this.cursorX = state.cursorX;
    this.cursorY = state.cursorY;
    this.scrollOffset = state.scrollOffset;

    return true;
  }

  /**
   * Get number of saved states
   */
  public getSavedStateCount(): number {
    return this.savedStates.length;
  }

  /**
   * Set cursor position
   */
  public setCursor(x: number, y: number): void {
    this.cursorX = Math.max(0, Math.min(x, this.width - 1));
    this.cursorY = Math.max(0, Math.min(y, this.height - 1));
  }

  /**
   * Get cursor position
   */
  public getCursor(): { x: number; y: number } {
    return { x: this.cursorX, y: this.cursorY };
  }

  /**
   * Move cursor relative to current position
   */
  public moveCursor(dx: number, dy: number): void {
    this.setCursor(this.cursorX + dx, this.cursorY + dy);
  }

  /**
   * Get buffer dimensions
   */
  public getDimensions(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  /**
   * Get entire buffer as 2D array
   */
  public getBuffer(): CharacterCell[][] {
    return this.buffer.map(line => line.map(cell => ({ ...cell })));
  }

  /**
   * Get buffer as string array
   */
  public getBufferAsStrings(): string[] {
    return this.buffer.map(line => line.map(cell => cell.char).join(''));
  }

  /**
   * Get scrollback buffer
   */
  public getScrollback(): CharacterCell[][] {
    return this.scrollback.map(line => line.map(cell => ({ ...cell })));
  }

  /**
   * Get scrollback as string array
   */
  public getScrollbackAsStrings(): string[] {
    return this.scrollback.map(line => line.map(cell => cell.char).join(''));
  }

  /**
   * Clear scrollback buffer
   */
  public clearScrollback(): void {
    this.scrollback = [];
  }

  /**
   * Get total lines (buffer + scrollback)
   */
  public getTotalLines(): number {
    return this.scrollback.length + this.height;
  }

  /**
   * Get line from combined buffer (scrollback + current)
   */
  public getAbsoluteLine(lineNumber: number): CharacterCell[] | null {
    if (lineNumber < 0) return null;

    if (lineNumber < this.scrollback.length) {
      return [...this.scrollback[lineNumber]];
    }

    const bufferLine = lineNumber - this.scrollback.length;
    if (bufferLine >= this.height) return null;

    return [...this.buffer[bufferLine]];
  }

  /**
   * Set scroll offset for viewing scrollback
   */
  public setScrollOffset(offset: number): void {
    this.scrollOffset = Math.max(0, Math.min(offset, this.scrollback.length));
  }

  /**
   * Get scroll offset
   */
  public getScrollOffset(): number {
    return this.scrollOffset;
  }

  /**
   * Scroll view up (into scrollback)
   */
  public scrollViewUp(lines: number = 1): void {
    this.setScrollOffset(this.scrollOffset + lines);
  }

  /**
   * Scroll view down (toward current)
   */
  public scrollViewDown(lines: number = 1): void {
    this.setScrollOffset(this.scrollOffset - lines);
  }

  /**
   * Scroll view to bottom (current buffer)
   */
  public scrollToBottom(): void {
    this.scrollOffset = 0;
  }

  /**
   * Scroll view to top (oldest scrollback)
   */
  public scrollToTop(): void {
    this.scrollOffset = this.scrollback.length;
  }

  /**
   * Check if at bottom of scroll
   */
  public isAtBottom(): boolean {
    return this.scrollOffset === 0;
  }

  /**
   * Fill entire screen with character
   */
  public fill(char: string, className?: string): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        this.write(x, y, char, className);
      }
    }
  }

  /**
   * Draw box using ASCII characters
   */
  public drawBox(
    x: number,
    y: number,
    width: number,
    height: number,
    className?: string
  ): void {
    // Top border
    this.write(x, y, '+', className);
    for (let i = 1; i < width - 1; i++) {
      this.write(x + i, y, '-', className);
    }
    this.write(x + width - 1, y, '+', className);

    // Sides
    for (let j = 1; j < height - 1; j++) {
      this.write(x, y + j, '|', className);
      this.write(x + width - 1, y + j, '|', className);
    }

    // Bottom border
    this.write(x, y + height - 1, '+', className);
    for (let i = 1; i < width - 1; i++) {
      this.write(x + i, y + height - 1, '-', className);
    }
    this.write(x + width - 1, y + height - 1, '+', className);
  }

  /**
   * Render buffer to HTML
   */
  public renderToHTML(): string {
    const lines: string[] = [];

    for (let y = 0; y < this.height; y++) {
      let lineHtml = '<div class="terminal-line">';
      let currentClass = '';
      let currentText = '';

      for (let x = 0; x < this.width; x++) {
        const cell = this.buffer[y][x];
        const cellClass = cell.className || '';

        if (cellClass !== currentClass) {
          // Flush current span
          if (currentText.length > 0) {
            if (currentClass) {
              lineHtml += `<span class="${currentClass}">${this.escapeHtml(currentText)}</span>`;
            } else {
              lineHtml += this.escapeHtml(currentText);
            }
            currentText = '';
          }
          currentClass = cellClass;
        }

        currentText += cell.char;
      }

      // Flush remaining text
      if (currentText.length > 0) {
        if (currentClass) {
          lineHtml += `<span class="${currentClass}">${this.escapeHtml(currentText)}</span>`;
        } else {
          lineHtml += this.escapeHtml(currentText);
        }
      }

      lineHtml += '</div>';
      lines.push(lineHtml);
    }

    return lines.join('\n');
  }

  /**
   * Escape HTML special characters
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

export default ScreenBuffer;
